import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Card,
  Input,
  List,
  Popover,
  Space,
  Spin,
  Switch,
  Tag,
  Typography,
  message,
} from 'antd';
import { PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  createSystemSetting,
  updateSystemSetting,
} from '../../actions/systemSettingActions';
import {
  DEFAULT_SESSION_NAME_CONFIG,
  SESSION_NAME_VARIABLES,
  UPLOAD_SESSION_NAME_SETTING_KEY,
  buildSessionNameFromConfig,
  getDefaultSegments,
  getVariableToken,
  normalizeSessionNameConfig,
  serializeSessionNameConfig,
  setSessionNameConfigCache,
} from '../crud/utils/sessionNameConfigUtils';
import useUploadSessionNameConfig from '../../hooks/useUploadSessionNameConfig';
import useNativeSortableList from '../../hooks/useNativeSortableList';
import SortableDragHandle from '../common/SortableDragHandle';
import { reorderByIndex } from '../../utils/sortableDropUtils';

const { Text, Paragraph } = Typography;

const DraggableSegmentList = ({ segments, onReorder, onRemove, leadingControls }) => {
  const handleReorder = useCallback((fromIndex, insertAt) => {
    onReorder(reorderByIndex(segments, fromIndex, insertAt));
  }, [onReorder, segments]);

  const {
    dragIndex,
    containerProps,
    setItemRef,
    getHandleProps,
    insertionIndicator,
  } = useNativeSortableList({
    orientation: 'horizontal',
    onReorder: handleReorder,
  });

  return (
    <div
      {...containerProps}
      style={{
        position: 'relative',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 8,
        minHeight: 40,
        padding: 8,
        border: '1px dashed var(--ant-color-border)',
        borderRadius: 6,
      }}
    >
      {insertionIndicator && <div aria-hidden {...insertionIndicator} />}
      {leadingControls}
      {segments.map((segment, index) => (
        <div key={`${segment.key}-${index}`} ref={setItemRef(index)}>
          <Tag
            closable
            onClose={(e) => {
              e.preventDefault();
              onRemove(index);
            }}
            style={{
              margin: 0,
              padding: '4px 10px',
              userSelect: 'none',
              opacity: dragIndex === index ? 0.45 : 1,
            }}
          >
            <Space size={6}>
              <SortableDragHandle
                ariaLabel="Drag to reorder segment"
                {...getHandleProps(index)}
              />
              <span>{getVariableToken(segment.key)}</span>
            </Space>
          </Tag>
        </div>
      ))}
    </div>
  );
};

const AddVariablePopover = ({ variables, onSelect, t }) => {
  const [open, setOpen] = useState(false);
  const addableCount = variables.filter((v) => !v.inPattern).length;

  return (
    <Popover
      content={(
        <List
          size="small"
          style={{ width: 280, maxHeight: 280, overflowY: 'auto' }}
          dataSource={variables}
          renderItem={(item) => (
            <List.Item
              style={{
                padding: '6px 0',
                cursor: item.inPattern ? 'not-allowed' : 'pointer',
                opacity: item.inPattern ? 0.45 : 1,
              }}
              onClick={() => {
                if (item.inPattern) return;
                onSelect(item.value);
                setOpen(false);
              }}
            >
              <Space direction="vertical" size={0}>
                <Text code style={{ fontSize: 12 }}>{item.token}</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>{item.description}</Text>
              </Space>
            </List.Item>
          )}
        />
      )}
      title={t('Add variable')}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
    >
      <Button type="dashed" size="small" icon={<PlusOutlined />} disabled={addableCount === 0}>
        {t('Add variable')}
      </Button>
    </Popover>
  );
};

const SessionNameManager = ({ embedded = false }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.userLogin?.userInfo);
  const { config: loadedConfig, settingId, setSettingId, loading, refresh } = useUploadSessionNameConfig();
  const [draft, setDraft] = useState(DEFAULT_SESSION_NAME_CONFIG);
  const [saving, setSaving] = useState(false);
  const { loading: creating } = useSelector((state) => state.systemSettingCreate || {});
  const { loading: updating } = useSelector((state) => state.systemSettingUpdate || {});

  useEffect(() => {
    setDraft(loadedConfig);
  }, [loadedConfig]);

  const preview = useMemo(() => buildSessionNameFromConfig(draft, userInfo), [draft, userInfo]);

  const availableVariables = useMemo(
    () =>
      Object.entries(SESSION_NAME_VARIABLES).map(([key, meta]) => ({
        value: key,
        token: meta.token,
        description: `${t(meta.labelKey)} → ${meta.example}`,
        inPattern: draft.segments.some((s) => s.key === key),
      })),
    [draft.segments, t]
  );

  const updateDraft = (changes) => {
    setDraft((prev) => normalizeSessionNameConfig({ ...prev, ...changes }));
  };

  const handleSave = async () => {
    const normalized = normalizeSessionNameConfig(draft);
    if (normalized.enabled && normalized.segments.length === 0) {
      message.error(t('Add at least one variable to the session name pattern'));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        settingKey: UPLOAD_SESSION_NAME_SETTING_KEY,
        settingValue: serializeSessionNameConfig(normalized),
        group: 'UploadSession',
        description: 'Upload session name pattern',
        isActive: true,
        lastUpdated: new Date().toISOString(),
      };
      if (settingId) {
        await dispatch(updateSystemSetting(settingId, { ...payload, id: settingId }));
      } else {
        const created = await dispatch(createSystemSetting(payload));
        setSettingId(created?.id ?? null);
      }
      setSessionNameConfigCache(normalized);
      setDraft(normalized);
      message.success(t('Upload session name configuration saved'));
      await refresh();
    } catch (err) {
      message.error(Array.isArray(err) ? err.join(', ') : err?.message || t('Failed to save configuration'));
    } finally {
      setSaving(false);
    }
  };

  const enableSwitch = (
    <Switch
      checked={draft.enabled}
      onChange={(enabled) => updateDraft({ enabled })}
      checkedChildren={t('Custom')}
      unCheckedChildren={t('Default')}
    />
  );

  const content = (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      {embedded && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text strong>{t('Upload Session Name Configuration')}</Text>
          {enableSwitch}
        </div>
      )}
      <Paragraph type="secondary" style={{ marginBottom: 0 }}>
        {t('Build the session name from variables. The uploader matches cohorts by exact name. Cohort dates follow the finest time part in the pattern.')}
      </Paragraph>

      <div>
        <Button
          size="small"
          disabled={!draft.enabled}
          onClick={() => updateDraft({ segments: getDefaultSegments() })}
        >
          {t('Load default')}
        </Button>
      </div>

      <Space wrap align="end">
        <div>
          <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>{t('Prefix')}</Text>
          <Input
            value={draft.prefix}
            onChange={(e) => updateDraft({ prefix: e.target.value })}
            style={{ width: 160 }}
            disabled={!draft.enabled}
          />
        </div>
        <div>
          <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>{t('Separator')}</Text>
          <Input
            value={draft.separator}
            onChange={(e) => updateDraft({ separator: e.target.value })}
            placeholder="-"
            style={{ width: 80 }}
            disabled={!draft.enabled}
          />
        </div>
      </Space>

      {draft.enabled && (
        <div>
          <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
            {t('Pattern')}
          </Text>
          <DraggableSegmentList
            segments={draft.segments}
            onReorder={(segments) => updateDraft({ segments })}
            onRemove={(index) => updateDraft({ segments: draft.segments.filter((_, i) => i !== index) })}
            leadingControls={(
              <AddVariablePopover
                variables={availableVariables}
                onSelect={(key) => {
                  if (!draft.segments.some((s) => s.key === key)) {
                    updateDraft({ segments: [...draft.segments, { type: 'variable', key }] });
                  }
                }}
                t={t}
              />
            )}
          />
        </div>
      )}

      <div>
        <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>{t('Preview')}</Text>
        <Text code style={{ fontSize: 14 }}>{preview}</Text>
      </div>

      <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving || creating || updating}>
        {t('Save configuration')}
      </Button>
    </Space>
  );

  if (loading) return <Spin />;
  if (embedded) return content;

  return (
    <Card size="small" title={t('Upload Session Name Configuration')} style={{ marginBottom: 16 }} extra={enableSwitch}>
      {content}
    </Card>
  );
};

export default SessionNameManager;
